import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import { useEffect, useRef, useState } from "react";
import type { UUID } from "@/Shared/types";
import { useCoupons } from "@/Shared/Coupons/Hooks/useCoupons";
import { useShipmentService } from "@/Services/ShipmentService";

// Carriers answer at their own pace, so we poll for up to a minute and a half
// rather than waiting on one long request.
const MAX_RETRIES = 90;
const RETRY_DELAY = 1_000;

/**
 * Polls for a shipment's quotes until the carriers have all answered.
 *
 * Results arrive incrementally — each poll returns everything so far, so quotes
 * appear on screen as they come in rather than all at the end.
 *
 * Two ways this stops early. Quoting can be switched off for an account, in
 * which case no quote will ever arrive and polling forever would just show a
 * spinner indefinitely; the caller shows an explanation instead. And a poll that
 * throws stops the loop rather than retrying, on the grounds that a failing
 * request is likely to keep failing.
 *
 * The coupon discount applied here is presentation only. The server applies the
 * real one at booking; this just means the customer sees the price they will pay.
 */
export function useBookShipmentQuotePolling(shipmentId: UUID) {
  const shipmentsService = useShipmentService();
  const { discountQuote } = useCoupons();

  const [quotes, setQuotes] = useState<Array<Quote> | undefined>();
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quotingDisabled, setQuotingDisabled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  async function loadQuotes(retryCount: number = 0) {
    if (retryCount >= MAX_RETRIES) {
      setLoadingQuotes(false);
      console.error("Quote loading exceeded maximum retries");
      return;
    }

    try {
      const quotesResponse = await shipmentsService.pollForQuotes(shipmentId);

      if (quotesResponse.quotingDisabled) {
        setQuotingDisabled(true);
        setLoadingQuotes(false);
        return;
      }

      setQuotes(
        quotesResponse.quotes.map(function (q) {
          return discountQuote(q);
        }),
      );

      if (!quotesResponse.complete) {
        timeoutRef.current = setTimeout(() => {
          loadQuotes(retryCount + 1);
        }, RETRY_DELAY);
      } else {
        setLoadingQuotes(false);
      }
    } catch (error) {
      console.error("Error loading quotes:", error);
      setLoadingQuotes(false);
    }
  }

  useEffect(() => {
    setLoadingQuotes(true);
    loadQuotes();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { quotes, loadingQuotes, quotingDisabled };
}
